import { searchCombinationNameOrId } from "@coworker/app/src/components/RoomSettings/services/localCombination.service";
import { CombinationProducts } from "@coworker/app/src/components/RoomSettings/types/article";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { parseProduct } from "@coworker/app/src/services/products.service";
import { ItemNoRegex } from "@coworker/apprestructured/src/scan-and-search/constants/sikConstants";
import { formatCombinationNumber } from "@coworker/apprestructured/src/scan-and-search/helpers/searchUtils";
import { useTransformSikSearch } from "@coworker/apprestructured/src/scan-and-search/hooks/search/useTransformSikSearch";
import { fetchItemInfoAndPriceSR } from "@coworker/apprestructured/src/shared/services/tasksApi";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { useQueries } from "@tanstack/react-query";

export const useSearchItems = (
  searchQuery: string,
  shouldHideSPRItems: boolean,
  shouldHideLocalCombinationItems: boolean,
  shouldSkipSikSearch: boolean = false
) => {
  const store_id = useStoreId();
  const countryId = useCountryId();
  const match = searchQuery.match(ItemNoRegex);
  const matchedQuery = match ? match.slice(1).join("") : searchQuery;
  const [itemNo] = parseProduct(matchedQuery);

  const { data, isLoading, fetchNextPage, isError } = useTransformSikSearch(
    searchQuery,
    shouldHideSPRItems,
    shouldSkipSikSearch
  );

  const shouldFetchFromOtherSources = !isLoading && data?.items?.length === 0;
  const shouldCheckLocalCombinations =
    !isLoading && !shouldHideLocalCombinationItems && !!matchedQuery;

  const sikReturnedWrongArticle =
    !!itemNo &&
    !isLoading &&
    !isError &&
    (data?.items?.length ?? 0) > 0 &&
    !data?.items?.some((item) => item.no === itemNo);

  const shouldFetchFromSR =
    shouldFetchFromOtherSources || sikReturnedWrongArticle;

  const [srItemsQuery, localCombinationQuery] = useQueries({
    queries: [
      {
        queryKey: ["item", "sr", itemNo],
        queryFn: async () => await fetchItemInfoAndPriceSR(itemNo),
        enabled: shouldFetchFromSR && !!itemNo,
        staleTime: 86400000, // Since this data does not change frequently, it can stay fresh for a long time
      },
      {
        queryKey: ["local_combinations", store_id, matchedQuery],
        queryFn: async () =>
          await searchCombinationNameOrId(store_id, matchedQuery),
        enabled: shouldCheckLocalCombinations,
        staleTime: 0,
      },
    ],
  });

  const combinationItems: Item[] =
    !localCombinationQuery?.isLoading &&
    !localCombinationQuery?.isError &&
    localCombinationQuery?.data
      ? (localCombinationQuery.data as CombinationProducts[]).map(
          (filteredLocalCombinations: CombinationProducts) => ({
            name:
              filteredLocalCombinations.combinationNames.find(
                (name) => name.country_code === countryId
              )?.combination_name ?? "",
            no: filteredLocalCombinations.combinationNumber,
            type: "SCI",
            noFormatted: formatCombinationNumber(
              filteredLocalCombinations.combinationNumber
            ),
            description: "",
            productTypeName: "",
            paNo: "",
            hfbNo: "",
            source: "data-service",
          })
        )
      : [];

  let finalItems: Item[] = [];

  if (
    !isLoading &&
    !isError &&
    data?.items &&
    data?.items.length > 0 &&
    !sikReturnedWrongArticle
  ) {
    const combinationNos = new Set(combinationItems.map((item) => item.no));
    // Correct the type of any SIK result that is also a registered local
    // combination, so it routes to the combination-specific product page.
    finalItems = data.items.map((item) =>
      combinationNos.has(item.no) ? { ...item, type: "SCI" } : item
    );
  } else if (shouldFetchFromSR) {
    if (
      !srItemsQuery?.isLoading &&
      !srItemsQuery?.isError &&
      srItemsQuery?.data
    ) {
      finalItems = [srItemsQuery.data];
    } else if (!shouldHideLocalCombinationItems && combinationItems.length) {
      finalItems = combinationItems;
    }
  }

  return {
    data: finalItems,
    isLoading:
      isLoading ||
      srItemsQuery?.isLoading ||
      (shouldCheckLocalCombinations && localCombinationQuery?.isLoading),
    isError: isError || srItemsQuery?.isError || localCombinationQuery?.isError,
    totalItems: data?.totalItems || finalItems.length,
    fetchNextPage,
  };
};
